import loadBackgroudImages from '../../common/loadBackgroudImages';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Autoplay, Navigation, Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { fetchCategories } from '../api/catalog';

// Get category image with fallback to existing images
const getCategoryImageUrl = (category) => {
  // Use API image if available
  if (category.image) {
    return category.image;
  }
  
  // Fallback to category slug-based images
  const imageMap = {
    'hair-pins-sticks': '/assets/imgs/elenard/categories/copper-hair-jewelry-owl.jpg',
    'dread-loc-jewelry': '/assets/imgs/elenard/categories/dreadlock-jewelry-snake.jpg',
    'fantasy-witchy': '/assets/imgs/elenard/categories/dreadlock-jewelry-snake.jpg',
    'gemstone-pieces': '/assets/imgs/elenard/categories/copper-hair-jewelry-owl.jpg',
    'new-arrivals': '/assets/imgs/elenard/categories/copper-hair-jewelry-owl.jpg',
  };
  
  return imageMap[category.slug] || '/assets/imgs/elenard/categories/copper-hair-jewelry-owl.jpg';
};

const getCategoryTarget = (category) => {
  const slug = String(category?.slug || category?.id || '').trim();
  if (!slug) return '/shop';

  // Requested example: clicking "Fantasy & witchy" should open this product page.
  if (slug === 'fantasy-witchy') return '/product/witchy-branch-hair-pin';

  return `/product/${encodeURIComponent(slug)}`;
};

export function HomePage() {
  const [storyExpanded, setStoryExpanded] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoriesExpanded, setCategoriesExpanded] = useState(false);

  useEffect(() => {
    loadBackgroudImages();
  }, []);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const data = await fetchCategories();
        setCategories(data);
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      }
    };
    loadCategories();
  }, []);

  const sliderOptions = {
    modules: [Navigation, Pagination, Autoplay],
    slidesPerView: 1,
    spaceBetween: 0,
    loop: true,
    speed: 800,
    autoplay: { delay: 3500, disableOnInteraction: false },
    navigation: true,
    pagination: { clickable: true },
    grabCursor: true,
  };

  const slides = [
    {
      src: '/assets/imgs/elenard/hero/Satia-and-Elena-main-picture-1.jpg',
      alt: 'Satia and Elena (1)',
    },
    {
      src: '/assets/imgs/elenard/hero/Elena-with-box-braids-2003.jpg',
      alt: 'Elena with box braids, 2003',
    },
    {
      src: '/assets/imgs/elenard/hero/Elena-with-box-braids-2005.jpg',
      alt: 'Elena with box braids, 2005',
    },
    {
      src: '/assets/imgs/elenard/hero/Elena-with-box-braids-2007.jpg',
      alt: 'Elena with box braids, 2007',
    },
    {
      src: '/assets/imgs/elenard/hero/Elena-with-box-braids-2008.jpg',
      alt: 'Elena with box braids, 2008',
    },
    {
      src: '/assets/imgs/elenard/hero/Elena-with-box-braids-2009.jpg',
      alt: 'Elena with box braids, 2009',
    },
  ];

  const storyParagraphs = useMemo(
    () => [
      'Hello! We are Satia and Elena, a husband-and-wife team creating high-detailed handmade copper jewelry for hair, dreadlocks, and braids that will last for years. Our collection includes copper hair pins (hair clips, hair accessories) designed as wearable art for people who love to express their individuality. Each hair jewelry we create is the little detail that makes your whole style feel like you!',
      'Our story began in 2013, when we met while working at a luxury hotel in the Seychelles. A few years later, we even got married there! In 2016, we moved to the Maldives and continued our careers at world-class resorts. Two of the three resorts where we worked were members of the Leading Hotels of the World organization, which means we were trained under some of the strictest service standards in hospitality. That experience shaped who we are today and taught us the true meaning of quality, attention to detail, and responsibility in everything we do.',
      'Life in luxury hotels also meant following strict grooming rules, which was challenging for Elena. Since childhood, she has loved expressive hairstyles, colorful looks, and unique accessories. In 2003, she got her first box braids and instantly felt more like herself. She returned to box braids again in 2005, 2007, 2008, and 2009, each time feeling the same strong connection to her style and identity. She dreamed of experimenting with more creative hairstyles, including colorful hair and dreadlocks, but her job didn’t allow it.',
      'Moving to Malaysia changed everything. Elena’s passion for expressive looks only grew stronger. In 2021, she discovered jewelry making, which inspired her to create pieces for people who love unique hairstyles, dreadlocks, and braids as much as she does.',
      'Over time, Elena perfected her craft and taught her husband Satia (me) to make hair jewelry as well. Elena’s designs are often inspired by the beauty of artistic forging, national patterns, and the avant-garde surrealist works of designer Elsa Schiaparelli and her brand. Together, we turn her ideas into sturdy, high-detailed copper wearable art.',
      'Among our collection you will also find fantasy style hair pins, witchy hair pins, and many more designs coming soon.',
      'The entire process of creating each hair pin can take up to 70 hours or more. Some pieces feature gemstones such as labradorite and moonstone. Every item is absolutely unique, with stone color, size, flash, and detail placement always varying. These headpieces are made to be long-lasting, cherished, and even passed down.',
      'We want our hair jewelry to add a unique touch to your style, show your character and individuality, help you express yourself, and make you feel even happier and more confident than you are now.',
      'Thank you for visiting our website and supporting our art! We put our heart into every piece of copper hair jewelry we create, and we hope you’ll love wearing it as much as we love making it.',
    ],
    []
  );

  const visibleCategories = useMemo(() => {
    if (!Array.isArray(categories)) return [];
    return categoriesExpanded ? categories : categories.slice(0, 4);
  }, [categories, categoriesExpanded]);

  return (
    <>
      <header
        className="page-header bg-img section-padding valign elenardHero"
        data-background="/assets/imgs/background/bg4.jpg"
        data-overlay-dark="8"
      >
        <div className="container pt-10">
          <div className="row">
            <div className="col-12">
              <div className="text-center">
                <h1 className="text-u ls1 fz-80 elenardHeroTitle">
                  Elena <span className="fw-200">RD</span>
                </h1>
                <p className="fz-18 text-white opacity-8 mt-10 elenardHeroSubtitle">
                  Art Jewelry &amp; Accessories
                </p>
              </div>
            </div>
          </div>

          <div className="row mt-30">
            <div className="col-12">
              <div className="elenardCarouselWrap">
                <div className="elenardCarousel sub-bg p-2 p-md-3">
                  <Swiper
                    {...sliderOptions}
                    className="elenardSwiper"
                    onSwiper={(sw) => {
                      try {
                        sw?.autoplay?.start?.();
                      } catch (_) {}
                    }}
                  >
                    {slides.map((s) => (
                      <SwiperSlide key={s.src}>
                        <div
                          className="elenardSlide"
                          style={{ '--elenard-bg': `url(${s.src})` }}
                        >
                          <div className="elenardSlideInner">
                            <img
                              decoding="async"
                              className="elenardCarouselImg"
                              src={s.src}
                              alt={s.alt}
                            />
                          </div>
                        </div>
                      </SwiperSlide>
                    ))}
                  </Swiper>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <section className="section-padding elenardSectionTight">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <div className="text-center mb-60">
                <h6 className="sub-title main-color mb-15">Elena RD</h6>
                <h2 className="fw-600">
                  Statement handmade jewelry for hair, dreads, locs and braids
                </h2>
              </div>
              <div className="elenardStory">
                {(storyExpanded ? storyParagraphs : storyParagraphs.slice(0, 1)).map(
                  (text, idx) => (
                    <p key={idx} className={idx === 0 ? 'fz-18' : 'mt-15'}>
                      {text}
                    </p>
                  )
                )}
                <div className="elenardStoryToggleWrap">
                  <button
                    type="button"
                    className="elenardStoryToggle"
                    onClick={() => setStoryExpanded((v) => !v)}
                    aria-expanded={storyExpanded}
                  >
                    {storyExpanded ? 'Read less' : 'Read more'}
                  </button>
                </div>
              </div>
              <div className="mt-40 d-flex flex-wrap">
                <a
                  href="/shop"
                  className="butn butn-md butn-bg main-colorbg radius-30"
                >
                  <span className="text">Shop Online</span>
                </a>
                <a
                  href="/contact"
                  className="butn butn-md butn-bord radius-30 ml-15"
                >
                  <span className="text">Contact Us</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="work-grid section-padding pt-0 elenardSectionTight">
        <div className="container">
          <div className="sec-head mb-50">
            <span className="sub-title main-color mb-15">Shop</span>
            <h3 className="fw-600 fz-50 text-u">
              Shop <span className="fw-200">categories</span>
            </h3>
          </div>

          <div className="row elenardCategoryGrid gx-4 gy-5">
            {visibleCategories.map((category) => (
              <div key={category.id} className="col-12 col-md-6 col-lg-6">
                <div className="item elenardCategoryCard h-100" style={{ width: '100%' }}>
                  <div className="img" style={{ height: '350px', overflow: 'hidden', borderRadius: '15px' }}>
                    <Link to={getCategoryTarget(category)} style={{ display: 'block', height: '100%' }}>
                      <img
                        src={getCategoryImageUrl(category)}
                        alt={category.name}
                        className="radius-15 elenardCategoryImg"
                        loading="lazy"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block'
                        }}
                      />
                    </Link>
                  </div>
                  <div className="cont mt-20 elenardCategoryCont">
                    <h5 className="mb-10">{category.name}</h5>
                    <Link to={getCategoryTarget(category)} className="underline">
                      View more
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-60">
            {categories.length > 4 ? (
              <button
                type="button"
                className="butn butn-md butn-bg main-colorbg radius-30"
                onClick={() => setCategoriesExpanded((v) => !v)}
                aria-expanded={categoriesExpanded}
              >
                <span className="text">{categoriesExpanded ? 'View Less' : 'View More'}</span>
              </button>
            ) : (
              <Link to="/shop" className="butn butn-md butn-bg main-colorbg radius-30">
                <span className="text">View More Products</span>
              </Link>
            )}
          </div>
        </div>
      </section>
    </>
  );
}

